import {
    ConfiguratorProduct,
    ConfiguratorProductAlternativeproducts,
    ConfiguratorProductMinleasingpermonth,
    ConfiguratorProductMinrentpermonth,
    ConfiguratorProductPrice1,
    ConfiguratorProductPrice3,
    CurrentConfiguration,
    CustomerContact,
    DefaultApi,
    Product,
    ProductFamilyIconsInner,
    UsedMainCategoriesInner
} from 'App/services/api';
import {
    AuthError,
    AuthResponse,
    DefaultApi as AuthApi,
    ErrorPayload,
    ResetPasswordResponse,
    SelectAccountResponse
} from 'App/services/auth';

import {ThunkDispatch} from "redux-thunk";
import {AnyAction, combineReducers} from "redux";
import * as categories from "./reducers/maincategories";
import * as specialoffers from "./reducers/specialoffers";
import * as subcategories from "./reducers/subcategories";
import * as products from "./reducers/products";
import * as configurations from "./reducers/configurations";
import {
    ActionType,
    API_BASE_PATH,
    ApiErrorType,
    AUTH_BASE_PATH,
    BASE_URL,
    ContactType,
    Currency,
    gtm,
    GTM_ACCOUNT_CREATE, GTM_CONTRACT, GTM_LEASING, GTM_OFFER, GTM_RENT, GTM_USER_LOGIN, Language,
    PORTAL_APP_VERSION
} from "App/constants";
import {createReducer, selectedLang} from "App/helper";
import * as JwtDecode from "jwt-decode";
import {selectConfigurePageAction} from "App/routes";
import {ApplicationState} from "App/store";
import {buildConfiguration, loadBasket, Basket} from "App/basket";
import {
    buildCustomerContact,
    contactFormCannotSubmit,
    contactFormReady,
    contactFormSubmitted,
    contactFormSubmitting
} from "App/contactData";
import {authReducer, AuthState} from "App/services/reducers/auth";
import {default as i18n} from "i18next";
import {subscribeReducer, SubscribeState} from "App/services/reducers/subscribe";
import {resetPasswordReducer, ResetPasswordState} from "App/services/reducers/resetpassword";

// Api stubs
export const stub = new DefaultApi({}, API_BASE_PATH);
export const auth = new AuthApi({}, AUTH_BASE_PATH);


export const enum FetchState { FETCHING, SUCCESS, FAILURE  }

export interface FetchAction {
    state: FetchState
    type: ActionType
    params?: any
    payload?: any
}

const fetchBegin = (type:ActionType, params:any):FetchAction => ({
    state: FetchState.FETCHING,
    type: type,
    params: params
});

const fetchSuccess = (type:ActionType, params:any, payload:any):FetchAction => ({
    state: FetchState.SUCCESS,
    type: type,
    params: params,
    payload: payload
});

const fetchFailure = (type:ActionType, error:Error):FetchAction => ({
    state: FetchState.FAILURE,
    type: type,
    payload: { ...error }
});

// Generic fetch
export async function fetch(dispatch: ThunkDispatch<any,null,AnyAction>, type:ActionType, params: any, fetch: Promise<any>) : Promise<any> {

    dispatch(fetchBegin(type, params));
    try {
        const data = await fetch;
        dispatch(fetchSuccess(type, params, data));
        return data
    } catch (e) {
        dispatch(fetchFailure(type, e));
    }
}
export function fetchConfiguration(purchaseId: number, productformId: number, usedmaincategoryId: number, productfamilyiconId: number) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>) {
        const configuration: CurrentConfiguration = await fetch(dispatch, ActionType.FETCH_CONFIGURATION, {purchaseId, productformId}, stub.retrieveConfiguration(purchaseId, productformId));
        if (configuration) {
            await fetch(dispatch, ActionType.FETCH_MAINCATEGORIES, null, stub.retrieveAllUsedMainCategories());
            await fetch(dispatch, ActionType.FETCH_PRODUCTFAMILYICONS, usedmaincategoryId, stub.retrievePfiByUsedMainCategory(usedmaincategoryId));
            await fetch(dispatch, ActionType.FETCH_PRODUCTSBYFAMILYICONS, productfamilyiconId, stub.retrieveProductsByProductFamilyIcon(productfamilyiconId));
            const product: ConfiguratorProduct = await fetch(dispatch, ActionType.FETCH_CONFIGURATORPRODUCT, configuration.productid, stub.retrieveConfiguratorProduct(configuration.productid));
            dispatch(loadBasket(configuration, product));
            dispatch(selectConfigurePageAction(usedmaincategoryId, productfamilyiconId, configuration.productid));
        }
    }
}
export function dismissConfiguration() {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>) {

        await fetch(dispatch, ActionType.DISMISS_CONFIGURATION, {}, stub.removeConfiguratorJWTCookie());
    }
}
export function submitConfiguration(type: ContactType) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        dispatch(contactFormSubmitting());
        var language: Language = selectedLang(i18n);
        const configuration: CurrentConfiguration | undefined = buildConfiguration(getState().basket, getState().contactData, language, getState().currency, type);
        const result = await fetch(dispatch, ActionType.CHECK_CONFIGURATION, {language: language, currency: getState().currency}, stub.submitConfiguration(configuration));
        if (result) {
            if (configuration&&configuration.rent) gtm(GTM_RENT);
            if (configuration&&configuration.leasing) gtm(GTM_LEASING);
            if (configuration&&configuration.customercontact.newportalaccountrequest) {
                await fetch(dispatch, ActionType.REQUEST_PORTAL_ACCOUNT, {}, stub.newPortalAccountRequest(configuration.customercontact));
            }
            gtm(type===ContactType.CONTRACT?GTM_CONTRACT:GTM_OFFER);
            await fetch(dispatch, ActionType.DISMISS_CONFIGURATION, {}, stub.removeConfiguratorJWTCookie());
            dispatch(contactFormSubmitted());
        }
    }
}

export function generateOfferPdf(basket: Basket, language: Language, currency: Currency) : any {
    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {
        var lang: Language = selectedLang(i18n);
        const configuration: CurrentConfiguration | undefined = buildConfiguration(getState().basket, getState().contactData, lang, getState().currency, ContactType.OFFER);
        const result = await fetch(dispatch, ActionType.CHECK_CONFIGURATION, {language: lang, currency: getState().currency}, stub.submitConfiguration(configuration));
        if (result && result.offerpdfurl) {
            window.open(BASE_URL + result.offerpdfurl, '_blank');
        }
    }
}

function errorCodeAuth(response: AuthResponse | AuthError | undefined): string {
    if (response && (<AuthResponse>response).error && (<ErrorPayload>(<AuthResponse>response).error).code===90) {
        // Application out of order
        return ApiErrorType.SERVER_UNAVAILABLE
    } else {
        // Defaults
        return response&&(<AuthError>response).errorcode||ApiErrorType.INVALID_CREDENTIALS;
    }
}

export function login(username:string, password:string) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>) {

        dispatch(fetchBegin(ActionType.LOGIN, username));
        try {
            const response: AuthResponse | AuthError | undefined = await auth.submitLogin({username: username, pwd: password, appversion: PORTAL_APP_VERSION});
            if (response && (<AuthResponse>response).retcode !== undefined && (<AuthResponse>response).retcode===0 &&
                (<AuthResponse>response).payload && (<AuthResponse>response).payload.authentication){
                dispatch(fetchSuccess(ActionType.LOGIN, username, (<AuthResponse>response).payload.authentication));
                gtm(GTM_USER_LOGIN);
            } else {
                dispatch(fetchFailure(ActionType.LOGIN, {name: errorCodeAuth(response), message:"Cannot login"}));
            }
        } catch (e) {
            dispatch(fetchFailure(ActionType.LOGIN, e));
        }
    }
}

export function logout(refresh: () => any) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>) {
        dispatch(fetchBegin(ActionType.LOGOUT,null));
        try {
            const response = await stub.logout();
            if (response ){
                dispatch(fetchSuccess(ActionType.LOGOUT, null,null));
                dispatch(dismissConfiguration());
                dispatch(refresh());
            } else {
                dispatch(fetchFailure(ActionType.LOGOUT, {name: errorCodeAuth(response), message:"Cannot perform logout"}));
            }
        } catch (e) {
            dispatch(fetchFailure(ActionType.LOGOUT, e));
        }
    }
}

export function selectAccount(authtenticationtoken:string, accountId:string, refresh: () => any) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>) {

        dispatch(fetchBegin(ActionType.SELECT_ACCOUNT, accountId));
        try {
            const response: SelectAccountResponse | AuthError | undefined = await auth.selectAccount({authtenticationtoken: authtenticationtoken, accountId: accountId, appversion: PORTAL_APP_VERSION});
            if (response && (<AuthResponse>response).retcode !== undefined && (<AuthResponse>response).retcode===0 &&
                (<AuthResponse>response).payload && (<AuthResponse>response).payload.account){
                dispatch(fetchSuccess(ActionType.SELECT_ACCOUNT, accountId, (<AuthResponse>response).payload.account));
                dispatch(refresh());
            } else {
                dispatch(fetchFailure(ActionType.SELECT_ACCOUNT, {name: errorCodeAuth(response), message:"Cannot select account"}));
            }
        } catch (e) {
            dispatch(fetchFailure(ActionType.SELECT_ACCOUNT, e));
        }
    }
}

export function subscribe() : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        dispatch(contactFormReady(true));
        dispatch(contactFormSubmitting());
        try {
            const contact: CustomerContact = buildCustomerContact(getState().contactData, ContactType.SUBSCRIBE);
            const result = await fetch(dispatch, ActionType.SUBSCRIBE, contact, stub.newPortalAccountRequest(contact));
            if (result) {
                dispatch(contactFormSubmitted(true));
                gtm(GTM_ACCOUNT_CREATE);
            } else {
                dispatch(contactFormCannotSubmit());
            }
        } catch (e) {
            dispatch(contactFormCannotSubmit());
        }
    }
}

export function resetpassword(username:string) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        try {
            const response: ResetPasswordResponse | AuthError | undefined = await auth.resetPassword({username: username, appversion: PORTAL_APP_VERSION});
            if (response && (<ResetPasswordResponse>response).retcode !== undefined && (<ResetPasswordResponse>response).retcode===0) {
                dispatch(fetchSuccess(ActionType.RESETPASSWORD, null, null));
            } else {
                dispatch(fetchFailure(ActionType.RESETPASSWORD, {name: ApiErrorType.SERVER_UNAVAILABLE, message:"Cannot reset password"}));
            }
        } catch (e) {
            dispatch(fetchFailure(ActionType.RESETPASSWORD, e));
        }
    }
}

/*
 * This is the root state of the app
 * It contains every substate of the app
 */
export interface State {
    categories: categories.State
    specialoffers: specialoffers.State | null
    subcategories: subcategories.State | null
    products: products.State | null
    configuration: configurations.State | null
    auth: AuthState | null
    subscribe: SubscribeState |null
    resetpassword: ResetPasswordState |null
}

/*
 * initialState of the app
 */
export const initialState: State = {
    categories: [],
    specialoffers: null,
    subcategories: null,
    products: null,
    configuration: null,
    auth: null,
    subscribe: null,
    resetpassword: null
};

// Slice reducers
const specialoffersReducer = createReducer([], {
    [ActionType.FETCH_SPECIALOFFERSCOMPACTOVERVIEW]: specialoffers.reducer
});

const categoriesReducer = createReducer([], {
    [ActionType.FETCH_MAINCATEGORIES]: categories.reducer
});

const subcategoriesReducer = createReducer([], {
    [ActionType.FETCH_PRODUCTFAMILYICONS]: subcategories.reducer,
    [ActionType.PAGE_MAIN]: subcategories.reducer
});

const productsReducer = createReducer([], {
    [ActionType.FETCH_PRODUCTSBYFAMILYICONS]: products.reducer,
    [ActionType.PAGE_CATEGORY]: products.reducer,
    [ActionType.PAGE_MAIN]: products.reducer,
    [ActionType.FILTER_PRODUCTS]: products.reducer,
    [ActionType.TOGGLE_FILTER]: products.reducer,
});

const configurationReducer = createReducer([], {
    [ActionType.FETCH_CONFIGURATORPRODUCT]: configurations.reducer,
    [ActionType.PAGE_SUBCATEGORY]: configurations.reducer,
    [ActionType.PAGE_CATEGORY]: configurations.reducer,
    [ActionType.PAGE_MAIN]: configurations.reducer,
});

/*
 * Root reducer of the app
 * Returned reducer will be of type Reducer<State>
 */
export const reducer = combineReducers<State>({
    categories: categoriesReducer,
    specialoffers: specialoffersReducer,
    subcategories: subcategoriesReducer,
    products: productsReducer,
    configuration: configurationReducer,
    auth: authReducer,
    subscribe: subscribeReducer,
    resetpassword: resetPasswordReducer
});


export function extractPrice(input: ConfiguratorProductPrice3 | undefined, currency: Currency): number | null {

    switch (currency) {
        case Currency.CHF: return input&&input.CHF?input.CHF:null;
        case Currency.EUR: return input&&input.EUR?input.EUR:null;
    }
    return null;
}

export function extractPrice1(input: ConfiguratorProductPrice1 | undefined, currency: Currency): number | null {

    switch (currency) {
        case Currency.CHF: return input&&input.CHF?input.CHF:null;
        case Currency.EUR: return input&&input.EUR?input.EUR:null;
    }
    return null;
}

export function extractRentPerMonth(input: ConfiguratorProductMinrentpermonth|undefined, currency: Currency): number | null {

    switch (currency) {
        case Currency.CHF: return input&&input.CHF?input.CHF:null;
        case Currency.EUR: return input&&input.EUR?input.EUR:null;
    }
    return null;
}

export function extractLeasingPerMonth(input: ConfiguratorProductMinleasingpermonth|undefined, currency: Currency): number | null {

    switch (currency) {
        case Currency.CHF: return input&&input.CHF?input.CHF:null;
        case Currency.EUR: return input&&input.EUR?input.EUR:null;
    }
    return null;
}

export function getSelectedMainCategoryData(state: State): UsedMainCategoriesInner | null {
    if (state.subcategories && state.subcategories.catId && state.categories) {
        for (let category of state.categories) {
            if (category.id===state.subcategories.catId) {
                return category;
            }
        }
    }
    return null;
}

export function getSelectedSubCategoryData(state: State): ProductFamilyIconsInner | null {
    if (state.products && state.products.subId && state.subcategories && state.subcategories.subcategory) {
        for (let subcategory of state.subcategories.subcategory) {
            if (subcategory.id===state.products.subId) {
                return subcategory;
            }
        }
    }
    return null;
}

function findProductById(state: State, prodId: number): Product | null {
    if (state.products && state.products.product) {
        for (let product of state.products.product) {
            if (product.id===prodId) {
                return product;
            }
        }
    }
    return null;
}
export function getSelectedProductData(state: State): Product | null {
    if (state.configuration && state.configuration.prodId && state.products && state.products.product) {
        return findProductById(state, state.configuration.prodId);
    }
    return null;
}

export interface SelectedAlternativeProduct {
    product: Product;

}

export function getSelectedAlternatives(state: State): Map<number, SelectedAlternativeProduct> {
    const result : Map<number, SelectedAlternativeProduct> = new Map<number, SelectedAlternativeProduct>();
    if (state.configuration && state.configuration.configuration.alternativeproducts && state.products && state.products.product) {
        const alternatives: Array<ConfiguratorProductAlternativeproducts> = state.configuration.configuration.alternativeproducts;
        for (let alternative of alternatives) {
            const product : Product | null = findProductById(state, alternative.alternativeproductid);
            if (product)
            {
                result.set(alternative.alternativeproductid, {product: product});
            }
        }
    }
    return result;
}

function loadCookies(): any {
    return document.cookie
        .split(';')
        .reduce((res, c) => {
            const [key, val] = c.trim().split('=').map(decodeURIComponent);
            try {
                return Object.assign(res, { [key]: JSON.parse(val) })
            } catch (e) {
                return Object.assign(res, { [key]: val })
            }
        }, {});
}

export function getConfigurationCookie(): any|null {
    const cookies: any = loadCookies();
    const configuratorjwttoken: string | undefined = cookies&&cookies.configuratorjwttoken;
    const decodedjwttoken: any | null = configuratorjwttoken ? JwtDecode(configuratorjwttoken) : null;
    return decodedjwttoken && decodedjwttoken.iss==='configurator'?decodedjwttoken:null;
}


// WEBPACK FOOTER //
// ./src/services/index.ts